import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Check } from 'lucide-react';
import { Card, Btn, Tag, Skeleton } from '../ui/primitives';
import DashboardNavbar from '../dashboardNavbar';
import { SYSTEM_TABS } from '../admin/adminSectionTabs';
import {
  fetchQuestionSelection,
  updateQuestionSelection,
} from '../services/questionSelectionApi';

/**
 * System → Question type selection (platform super admins / userType 2).
 *
 * Sets the question mix the fully-automatic chatbot flow uses for Non-Technical
 * courses. A type set to 0 is simply not generated, so e.g. 4 direct / 4 scenario /
 * 2 image / 0 passage produces a set with no passage questions.
 */

const TYPE_LABELS = {
  direct: 'Direct',
  scenario: 'Scenario',
  image: 'Image',
  passage: 'Passage',
  total: 'Total',
};

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13.5px;

  th, td {
    text-align: left;
    padding: 10px 12px;
    border-bottom: 1px solid ${({ theme }) => theme.border};
    vertical-align: middle;
  }

  th {
    color: ${({ theme }) => theme.textSubtle};
    font-weight: 700;
    font-size: ${({ theme }) => theme.fsMicro};
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  tr.total-row td {
    font-weight: 700;
    background: ${({ theme }) => theme.surface2};
  }

  .key {
    font-size: 12px;
    color: ${({ theme }) => theme.textSubtle};
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  }
`;

const CountInput = styled.input`
  width: 90px;
  padding: 7px 10px;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.text};
  font-size: 13.5px;
`;

const Msg = styled.p`
  margin: 10px 2px 0;
  font-size: 13px;
  color: ${({ $error, theme }) => ($error ? '#ef4444' : theme.accentStrong || '#1b7f4b')};
`;

function QuestionSelectionTable() {
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchQuestionSelection();
      setRows(data);
      setDrafts(data.reduce((acc, row) => ({ ...acc, [row.id]: String(row.questionCount ?? '') }), {}));
    } catch (e) {
      setError(e.message || 'Could not load the question type selection.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async (row) => {
    const next = Number.parseInt(drafts[row.id], 10);
    if (!Number.isFinite(next) || next < 0) {
      setError('Enter a whole number of 0 or more.');
      return;
    }
    setSavingId(row.id);
    setError('');
    setMessage('');
    try {
      const saved = await updateQuestionSelection(row.id, next);
      setRows((prev) => prev.map((r) => (r.id === saved.id ? saved : r)));
      setMessage(`Saved ${TYPE_LABELS[String(saved.questionType).toLowerCase()] || saved.questionType}.`);
    } catch (e) {
      setError(e.message || 'Save failed.');
    } finally {
      setSavingId(null);
    }
  };

  // The per-type rows should add up to the total row; flag it when they don't.
  const typeRows = rows.filter((r) => String(r.questionType).toLowerCase() !== 'total');
  const totalRow = rows.find((r) => String(r.questionType).toLowerCase() === 'total');
  const typeSum = typeRows.reduce((sum, r) => sum + (Number(r.questionCount) || 0), 0);
  const mismatch = totalRow && typeSum !== Number(totalRow.questionCount);

  return (
    <Card $pad>
      {error && <Msg $error>{error}</Msg>}
      {message && <Msg>{message}</Msg>}

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Skeleton $w="100%" $h="42px" />
          <Skeleton $w="100%" $h="42px" />
          <Skeleton $w="100%" $h="42px" />
        </div>
      ) : (
        <>
          {mismatch && (
            <Msg $error>
              The per-type counts add up to {typeSum}, but Total is {totalRow.questionCount}.
              Generation uses at most Total questions.
            </Msg>
          )}
          <Table>
            <thead>
              <tr>
                <th style={{ width: '40%' }}>Question type</th>
                <th style={{ width: '25%' }}>Id</th>
                <th style={{ width: '20%' }}>Count</th>
                <th style={{ width: '15%' }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const type = String(row.questionType || '').toLowerCase();
                const isTotal = type === 'total';
                const dirty = String(drafts[row.id] ?? '') !== String(row.questionCount ?? '');
                return (
                  <tr key={row.id} className={isTotal ? 'total-row' : undefined}>
                    <td>
                      {TYPE_LABELS[type] || row.questionType}
                      {!isTotal && Number(row.questionCount) === 0 && (
                        <Tag $tone="gray" style={{ marginLeft: 8 }}>Not generated</Tag>
                      )}
                    </td>
                    <td className="key">{row.questionTypeId}</td>
                    <td>
                      <CountInput
                        type="number"
                        min="0"
                        value={drafts[row.id] ?? ''}
                        onChange={(e) => setDrafts((prev) => ({ ...prev, [row.id]: e.target.value }))}
                        aria-label={`${TYPE_LABELS[type] || row.questionType} count`}
                      />
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Btn
                        $variant={dirty ? undefined : 'ghost'}
                        onClick={() => handleSave(row)}
                        disabled={savingId === row.id || !dirty}
                      >
                        {savingId === row.id ? 'Saving…' : <><Check size={14} /> Save</>}
                      </Btn>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </>
      )}
    </Card>
  );
}

export default function QuestionSelectionManager() {
  return (
    <div>
      <DashboardNavbar menuItems={SYSTEM_TABS} />
      <Shell>
        <Inner>
          <PageIntro>
            <h2>Question type selection</h2>
            <p>
              The question mix the chatbot&apos;s fully-automatic course creation uses for
              <b> Non-Technical</b> courses. Set a type to 0 and it will not be generated —
              e.g. 4 Direct, 4 Scenario, 2 Image and 0 Passage produces a set with no
              passage questions. Technical courses and K-12 ELA keep their own fixed mixes.
            </p>
          </PageIntro>
          <QuestionSelectionTable />
        </Inner>
      </Shell>
    </div>
  );
}

const Shell = styled.div`
  padding: 0 28px 36px;
  color: ${({ theme }) => theme.text};
  font-family: ${({ theme }) => theme.fontBody};
  @media (max-width: 768px) {
    padding: 0 14px 28px;
  }
`;

const Inner = styled.div`
  width: min(1180px, 95%);
  margin: 0 auto;
`;

const PageIntro = styled.div`
  margin: 4px 0 22px;

  h2 {
    margin: 0 0 6px;
    font-family: ${({ theme }) => theme.fontHead};
    font-size: 20px;
    font-weight: 700;
    letter-spacing: -0.01em;
  }

  p {
    margin: 0;
    font-size: ${({ theme }) => theme.fsSm};
    color: ${({ theme }) => theme.textMuted};
    line-height: 1.5;
  }
`;
